import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ChevronDown, Check, Copy, ArrowLeft, Shield, Camera, X } from "lucide-react";
import { uploadFiles } from "../shared/cloudinaryUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Creation form is available in 3 languages (Norwegian / English / Romanian).
const LANGS = [
  { code: "no", flag: "🇳🇴", name: "Norsk" },
  { code: "en", flag: "🇬🇧", name: "English" },
  { code: "ro", flag: "🇷🇴", name: "Română" },
];

const COMMON_HAZARDS = [
  { id: "fall_height",     no: "Arbeid i høyden, stillas, tak",     en: "Work at height, scaffolding, roof", ro: "Lucru la înălțime, schele, acoperiș" },
  { id: "falling_objects", no: "Fallende gjenstander",              en: "Falling objects",                   ro: "Obiecte care cad" },
  { id: "weather",         no: "Vær (vind, regn, is, kulde)",       en: "Weather (wind, rain, ice, cold)",   ro: "Vreme (vânt, ploaie, gheață, frig)" },
  { id: "traffic",         no: "Trafikk / fotgjengere",             en: "Traffic / pedestrians",             ro: "Trafic / pietoni" },
  { id: "electrical",      no: "Strøm / elektrisk utstyr",          en: "Electricity / electrical equipment", ro: "Curent / echipament electric" },
  { id: "manual_handling", no: "Tunge løft, belastning",            en: "Heavy lifting, strain",             ro: "Ridicări grele, efort" },
  { id: "confined_space",  no: "Trange rom, dårlig ventilasjon",    en: "Confined spaces, poor ventilation", ro: "Spații înguste, ventilație slabă" },
  { id: "noise",           no: "Høy støy",                          en: "Loud noise",                        ro: "Zgomot puternic" },
  { id: "chemicals",       no: "Kjemikalier / støv",                en: "Chemicals / dust",                  ro: "Substanțe chimice / praf" },
  { id: "other",           no: "Andre farer",                       en: "Other hazards",                     ro: "Alte pericole" },
];

const PPE_OPTIONS = [
  { id: "helmet",       icon: "🪖", no: "Hjelm",       en: "Helmet",             ro: "Cască" },
  { id: "harness",      icon: "🦺", no: "Fallsele",    en: "Harness",            ro: "Ham de siguranță" },
  { id: "gloves",       icon: "🧤", no: "Hansker",     en: "Gloves",             ro: "Mănuși" },
  { id: "safety_shoes", icon: "👢", no: "Vernesko",    en: "Safety shoes",       ro: "Bocanci de protecție" },
  { id: "hi_vis",       icon: "🦺", no: "Refleksvest", en: "Hi-vis vest",        ro: "Vestă reflectorizantă" },
  { id: "glasses",      icon: "🥽", no: "Vernebriller",en: "Safety glasses",     ro: "Ochelari de protecție" },
  { id: "hearing",      icon: "🎧", no: "Hørselvern",  en: "Hearing protection", ro: "Antifoane" },
  { id: "mask",         icon: "😷", no: "Maske",       en: "Mask",               ro: "Mască" },
];

const RISK_MATRIX = {
  low: { low: "green", medium: "yellow", high: "yellow" },
  medium: { low: "yellow", medium: "yellow", high: "red" },
  high: { low: "yellow", medium: "red", high: "red" },
};
const RISK_COLORS = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-red-500" };
const LEVELS = [
  { k: "low",    no: "Lav",     en: "Low",    ro: "Mic" },
  { k: "medium", no: "Middels", en: "Medium", ro: "Mediu" },
  { k: "high",   no: "Høy",     en: "High",   ro: "Mare" },
];
const RISK_WORD = {
  green: { no: "LAV", en: "LOW", ro: "MIC" },
  red:   { no: "HØY", en: "HIGH", ro: "MARE" },
  yellow:{ no: "MEDIUM", en: "MEDIUM", ro: "MEDIU" },
};

const TR = {
  no: { subtitle: "SJA — Sikker Jobb Analyse", back: "Tilbake til Doserne", import_prev: "Importer fra tidligere SJA", select_project: "Velg prosjekt", project_ph: "— Søk eller velg prosjekt —", autofill: "Adresse, Kunde og Arbeidssted fylles ut automatisk. Eller fyll ut manuelt under.", no_projects_pre: "Ingen prosjekter i katalogen ennå. Fyll ut manuelt under, eller ", no_projects_link: "legg til prosjekter fra admin", change: "Bytt", search: "Søk...", no_hits: "Ingen treff", job: "Arbeidsbeskrivelse", job_ph: "Hva skal utføres?", worksite: "Arbeidssted", worksite_ph: "Adresse / lokasjon", date: "Dato", prepared_by: "Utarbeidet av", name: "Navn", hazard_mapping: "Farekartlegging", custom: "+ Egendefinert", already_added: "Allerede lagt til", hazard_desc_ph: "Beskrivelse av faren", desc_label: "Beskrivelse", measures_label: "Forebyggende tiltak", probability: "Sannsynlighet", consequence: "Konsekvens", measures_ph: "Forebyggende tiltak", responsible_ph: "Ansvarlig person", ppe: "Personlig verneutstyr", emergency: "Beredskap", emg_ph: "Nødkontakt (f.eks. 113)", hospital_ph: "Nærmeste sykehus", firstaid_ph: "Førstehjelpskofferten er:", additional_ph: "Ytterligere tiltak", photos: "Bilder", from_progress: "Fra progresjon", upload: "Last opp", no_photos: "Ingen bilder ennå.", pick_project_first: "Velg prosjekt først", save: "Lagre og generer signeringslink", saving: "Lagrer...", locked: "SJA er ferdigstilt/signert og kan ikke endres.", fill_required: "Fyll ut jobb, ansvarlig og minst én fare", updated: "Oppdatert", error: "Feil", upload_failed: "Opplasting feilet", photos_failed: "Kunne ikke hente bilder", load_failed: "Kunne ikke laste SJA", imported_from: "Importert fra", sja_fetch_failed: "Kunne ikke hente SJA", pick_photos: "Velg bilder fra progresjon", no_progress_photos: "Ingen progresjonsbilder for dette prosjektet ennå.", cancel: "Avbryt", add: "Legg til", import_search_ph: "Søk kode / adresse / jobb...", import_note: "Farer, verneutstyr og tiltak kopieres. Deltakere og bilder starter på nytt.", no_sja_found: "Ingen SJA funnet.", hazards_word: "farer", saved: "Lagret!", sign_link_team: "Signeringslink for team", continue_edit: "Fortsett å redigere", edit: "Rediger", open_sign: "Åpne signeringslink →", start_new: "Start ny SJA" },
  en: { subtitle: "SJA — Safe Job Analysis", back: "Back to Doserne", import_prev: "Import from previous SJA", select_project: "Select project", project_ph: "— Search or select project —", autofill: "Address, Customer and Work site are filled in automatically. Or fill in manually below.", no_projects_pre: "No projects in the catalog yet. Fill in manually below, or ", no_projects_link: "add projects from admin", change: "Change", search: "Search...", no_hits: "No results", job: "Job description", job_ph: "What will be done?", worksite: "Work site", worksite_ph: "Address / location", date: "Date", prepared_by: "Prepared by", name: "Name", hazard_mapping: "Hazard mapping", custom: "+ Custom", already_added: "Already added", hazard_desc_ph: "Description of the hazard", desc_label: "Description", measures_label: "Preventive measures", probability: "Probability", consequence: "Consequence", measures_ph: "Preventive measures", responsible_ph: "Responsible person", ppe: "Personal protective equipment", emergency: "Emergency", emg_ph: "Emergency contact (e.g. 113)", hospital_ph: "Nearest hospital", firstaid_ph: "First aid kit is:", additional_ph: "Additional measures", photos: "Photos", from_progress: "From progress", upload: "Upload", no_photos: "No photos yet.", pick_project_first: "Select a project first", save: "Save and generate signing link", saving: "Saving...", locked: "The SJA is finalized/signed and cannot be changed.", fill_required: "Fill in the job, responsible person and at least one hazard", updated: "Updated", error: "Error", upload_failed: "Upload failed", photos_failed: "Could not fetch photos", load_failed: "Could not load SJA", imported_from: "Imported from", sja_fetch_failed: "Could not fetch SJAs", pick_photos: "Select photos from progress", no_progress_photos: "No progress photos for this project yet.", cancel: "Cancel", add: "Add", import_search_ph: "Search code / address / job...", import_note: "Hazards, PPE and measures are copied. Participants and photos start fresh.", no_sja_found: "No SJA found.", hazards_word: "hazards", saved: "Saved!", sign_link_team: "Signing link for the team", continue_edit: "Continue editing", edit: "Edit", open_sign: "Open signing link →", start_new: "Start new SJA" },
  ro: { subtitle: "SJA — Analiza Siguranței Lucrării", back: "Înapoi la Doserne", import_prev: "Importă dintr-un SJA anterior", select_project: "Alege proiectul", project_ph: "— Caută sau alege proiectul —", autofill: "Adresa, Clientul și Locul lucrării se completează automat. Sau completează manual mai jos.", no_projects_pre: "Niciun proiect în catalog încă. Completează manual mai jos, sau ", no_projects_link: "adaugă proiecte din admin", change: "Schimbă", search: "Caută...", no_hits: "Niciun rezultat", job: "Descrierea lucrării", job_ph: "Ce se va executa?", worksite: "Locul lucrării", worksite_ph: "Adresă / locație", date: "Data", prepared_by: "Întocmit de", name: "Nume", hazard_mapping: "Identificarea pericolelor", custom: "+ Personalizat", already_added: "Deja adăugat", hazard_desc_ph: "Descrierea pericolului", desc_label: "Descriere", measures_label: "Măsuri preventive", probability: "Probabilitate", consequence: "Consecință", measures_ph: "Măsuri preventive", responsible_ph: "Persoana responsabilă", ppe: "Echipament individual de protecție", emergency: "Urgență", emg_ph: "Contact urgență (ex. 113)", hospital_ph: "Cel mai apropiat spital", firstaid_ph: "Trusa de prim ajutor este:", additional_ph: "Măsuri suplimentare", photos: "Poze", from_progress: "Din progres", upload: "Încarcă", no_photos: "Nicio poză încă.", pick_project_first: "Alege întâi un proiect", save: "Salvează și generează linkul de semnare", saving: "Se salvează...", locked: "SJA este finalizat/semnat și nu poate fi modificat.", fill_required: "Completează lucrarea, responsabilul și cel puțin un pericol", updated: "Actualizat", error: "Eroare", upload_failed: "Încărcarea a eșuat", photos_failed: "Nu s-au putut prelua pozele", load_failed: "Nu s-a putut încărca SJA", imported_from: "Importat din", sja_fetch_failed: "Nu s-au putut prelua SJA-urile", pick_photos: "Alege poze din progres", no_progress_photos: "Încă nu există poze de progres pentru acest proiect.", cancel: "Anulează", add: "Adaugă", import_search_ph: "Caută cod / adresă / lucrare...", import_note: "Pericolele, echipamentul și măsurile se copiază. Participanții și pozele încep de la zero.", no_sja_found: "Niciun SJA găsit.", hazards_word: "pericole", saved: "Salvat!", sign_link_team: "Link de semnare pentru echipă", continue_edit: "Continuă editarea", edit: "Editează", open_sign: "Deschide linkul de semnare →", start_new: "Începe un SJA nou" },
};

const emptyHazard = () => ({ id: "", description: "", probability: "medium", consequence: "medium", preventive_measures: "", responsible: "" });

const emptyForm = () => ({
  language: "no", project_id: null,
  address: "", customer: "", customer_email: "",
  job_description: "", work_location: "",
  work_date: new Date().toISOString().slice(0, 10),
  hazards: [],
  ppe_required: ["helmet", "safety_shoes", "hi_vis"],
  additional_measures: "",
  emergency_contact: "113",
  nearest_hospital: "",
  first_aid_location: "",
  prepared_by: "",
  prepared_date: new Date().toISOString().slice(0, 10),
  participants: [],
  photos: [],
});

function ProjectSelect({ projects, onPick, placeholder, tt }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return projects;
    return projects.filter((p) => [p.address, p.customer, p.customer_email, p.name].some((f) => (f || "").toLowerCase().includes(s)));
  }, [q, projects]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-amber-300 rounded-md text-left text-sm hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
        data-testid="select-project">
        <span className="text-slate-500">{placeholder}</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tt("search")}
              className="w-full px-2 py-1.5 text-sm text-slate-900 border border-slate-200 rounded" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <div className="px-3 py-3 text-sm text-slate-400 italic">{tt("no_hits")}</div>}
            {filtered.map((p) => (
              <button type="button" key={p.id}
                onClick={() => { onPick(p); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-amber-50 border-b border-slate-50 last:border-0">
                <div className="font-semibold">{p.address}</div>
                <div className="text-xs text-slate-500">{p.customer}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SJAForm() {
  const navigate = useNavigate();
  const { editToken } = useParams();
  const [searchParams] = useSearchParams();
  const prefilledProjectId = searchParams.get("project_id");
  const [form, setForm] = useState(emptyForm);
  const [lang, setLang] = useState("no");
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({ edit: false, sign: false });
  const [uploading, setUploading] = useState(false);
  const progressPickedRef = useRef(new Set());
  const [progress, setProgress] = useState({ open: false, loading: false, photos: [], sel: new Set() });
  const [importer, setImporter] = useState({ open: false, loading: false, q: "", items: [] });
  const readOnly = form.status === "finalized" || form.status === "sent";

  const t = (k) => (TR[lang] || TR.no)[k] || TR.no[k] || k;
  const L = (o) => o[lang] || o.no;
  const pickLang = (code) => { setLang(code); setForm((f) => ({ ...f, language: code })); };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/underlag/projects`);
        const list = data.projects || [];
        setProjects(list);
        if (prefilledProjectId && !editToken) {
          const p = list.find((x) => x.id === prefilledProjectId);
          if (p) {
            setForm((f) => ({
              ...f,
              project_id: p.id,
              address: p.address || "",
              customer: p.customer || "",
              customer_email: p.customer_email || "",
            }));
          }
        }
      } catch { /* silent */ }
    })();
    if (editToken) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/sja/edit/${editToken}`);
          setForm({ ...emptyForm(), ...data });
          if (data.language && TR[data.language]) setLang(data.language);
        } catch { toast.error(TR.no.load_failed); }
      })();
    }
  }, [editToken, prefilledProjectId]);

  const addHazardCommon = (h) => {
    if (form.hazards.some((x) => x.id === h.id)) { toast.info(t("already_added")); return; }
    setForm((f) => ({ ...f, hazards: [...f.hazards, { ...emptyHazard(), id: h.id, description: L(h) }] }));
  };
  const addHazardCustom = () => setForm((f) => ({ ...f, hazards: [...f.hazards, { ...emptyHazard(), id: "custom", description: "" }] }));
  const updateHazard = (i, patch) => setForm((f) => ({ ...f, hazards: f.hazards.map((h, x) => (x === i ? { ...h, ...patch } : h)) }));
  const removeHazard = (i) => setForm((f) => ({ ...f, hazards: f.hazards.filter((_, x) => x !== i) }));
  const togglePPE = (id) => setForm((f) => ({ ...f, ppe_required: f.ppe_required.includes(id) ? f.ppe_required.filter((p) => p !== id) : [...f.ppe_required, id] }));

  const canSave = () => form.job_description.trim() && form.prepared_by.trim() && form.hazards.length > 0;

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadFiles(files, "uploads/sja", API, axios);
      const urls = uploaded.map((u) => u.url);
      setForm((f) => ({ ...f, photos: [...(f.photos || []), ...urls] }));
    } catch { toast.error(t("upload_failed")); }
    setUploading(false);
  };
  const removePhoto = (pi) => setForm((f) => ({ ...f, photos: (f.photos || []).filter((_, x) => x !== pi) }));
  const openProgressPicker = async () => {
    if (!form.project_id) { toast.error(t("pick_project_first")); return; }
    setProgress({ open: true, loading: true, photos: [], sel: new Set() });
    try {
      const { data } = await axios.get(`${API}/underlag/project-progress-photos`, { params: { project_id: form.project_id } });
      setProgress((p) => ({ ...p, loading: false, photos: data.photos || [] }));
    } catch { toast.error(t("photos_failed")); setProgress((p) => ({ ...p, loading: false, photos: [] })); }
  };
  const toggleProgressSel = (url) => setProgress((p) => {
    const sel = new Set(p.sel); if (sel.has(url)) sel.delete(url); else sel.add(url); return { ...p, sel };
  });
  const addProgressSelected = () => {
    const existing = new Set(form.photos || []);
    const toAdd = progress.photos.filter((ph) => progress.sel.has(ph.url) && !existing.has(ph.url)).map((ph) => ph.url);
    toAdd.forEach((u) => progressPickedRef.current.add(u));
    setForm((f) => ({ ...f, photos: [...(f.photos || []), ...toAdd] }));
    setProgress({ open: false, loading: false, photos: [], sel: new Set() });
    if (toAdd.length) toast.success(`+${toAdd.length}`);
  };

  const openImporter = async () => {
    setImporter({ open: true, loading: true, q: "", items: [] });
    try {
      const { data } = await axios.get(`${API}/sja/import-list`, { params: { project_id: form.project_id || "" } });
      setImporter((s) => ({ ...s, loading: false, items: data.items || [] }));
    } catch { toast.error(t("sja_fetch_failed")); setImporter((s) => ({ ...s, loading: false, items: [] })); }
  };
  const applyImport = (it) => {
    setForm((f) => ({
      ...f,
      address: f.address || it.address || "",
      customer: f.customer || it.customer || "",
      customer_email: f.customer_email || it.customer_email || "",
      project_id: f.project_id || it.project_id || null,
      job_description: it.job_description || f.job_description,
      work_location: it.work_location || f.work_location,
      hazards: (it.hazards || []).map((h) => ({ ...h })),
      ppe_required: [...(it.ppe_required || [])],
      additional_measures: it.additional_measures || "",
      emergency_contact: it.emergency_contact || f.emergency_contact,
      nearest_hospital: it.nearest_hospital || "",
      first_aid_location: it.first_aid_location || "",
      participants: [],
      photos: [],
    }));
    setImporter({ open: false, loading: false, q: "", items: [] });
    toast.success(`${t("imported_from")} ${it.code}`);
  };

  const save = async () => {
    if (!canSave()) { toast.error(t("fill_required")); return; }
    setSaving(true);
    try {
      const picked = progressPickedRef.current;
      const pcPhotos = (form.photos || []).filter((u) => u && !picked.has(u));
      const payload = { ...form, language: lang, progress_post_photos: pcPhotos };
      if (editToken) {
        await axios.put(`${API}/sja/edit/${editToken}`, payload);
        toast.success(t("updated"));
      } else {
        const { data } = await axios.post(`${API}/sja/create`, payload);
        setResult(data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setSaving(false);
  };

  const copyLink = (url, kind) => {
    navigator.clipboard.writeText(url);
    setCopied({ ...copied, [kind]: true });
    setTimeout(() => setCopied({ edit: false, sign: false }), 2000);
  };

  return (
    <div className="light-theme min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16 text-slate-900">
      <div className="sticky top-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center gap-3 shadow-md">
        <button onClick={() => navigate("/doserne")} className="p-1 hover:bg-white/10 rounded" style={{ color: "#fff" }} data-testid="back-btn" title={t("back")}><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Doserne Stillas AS</div>
          <h1 className="text-base font-bold" style={{ color: "#fff" }}>{t("subtitle")}</h1>
        </div>
        <div className="flex items-center gap-1">
          {LANGS.map((l) => (
            <button key={l.code} type="button" onClick={() => pickLang(l.code)}
              className={`px-2 py-1 rounded-lg text-xs font-bold transition ${lang === l.code ? "bg-amber-400 text-slate-900" : "bg-white/10 text-white hover:bg-white/20"}`}
              data-testid={`sjaform-lang-${l.code}`} title={l.name}>
              {l.flag}
            </button>
          ))}
        </div>
        <Shield className="w-6 h-6 text-amber-300 flex-shrink-0" />
      </div>

      {readOnly && (
        <div className="max-w-4xl mx-auto px-4 pt-4" data-testid="sja-locked-banner">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-3 text-sm font-semibold">
            <Shield className="w-4 h-4 flex-shrink-0" />
            {t("locked")}
          </div>
        </div>
      )}
      <fieldset disabled={readOnly} className="max-w-4xl mx-auto p-4 space-y-4 border-0 m-0 min-w-0 disabled:opacity-60">
        <div className="flex justify-end">
          <button type="button" onClick={openImporter}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-400 text-amber-700 text-xs font-bold rounded-md hover:bg-amber-50"
            data-testid="import-sja-btn">
            <Copy className="w-3.5 h-3.5" /> {t("import_prev")}
          </button>
        </div>
        {/* Step 1: Project selector */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-amber-300 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center font-black text-sm shadow-sm" style={{ color: "#fff" }}>1</div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-bold text-slate-900 mb-1">{t("select_project")}</label>
              {projects.length > 0 ? (
                <>
                  <ProjectSelect projects={projects} placeholder={t("project_ph")} tt={t}
                    onPick={(p) => { setForm((f) => ({ ...f, project_id: p.id, address: p.address, customer: p.customer, customer_email: p.customer_email, work_location: p.address })); toast.success(p.address); }} />
                  <p className="text-xs text-slate-500 mt-1">{t("autofill")}</p>
                </>
              ) : (
                <p className="text-sm text-slate-500 italic">{t("no_projects_pre")}<a href="/admin/underlag" className="text-amber-600 underline">{t("no_projects_link")}</a>.</p>
              )}
              {form.project_id && (
                <div className="mt-2 flex items-center gap-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs">
                  <span className="text-amber-800 font-semibold">✓ {form.address}</span>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, project_id: null }))}
                    className="ml-auto text-amber-700 hover:text-amber-900 text-xs" data-testid="clear-project-btn">{t("change")}</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 1: Basic */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t("job")} *</label>
              <textarea value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })}
                placeholder={t("job_ph")} className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" rows={2} data-testid="input-job" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t("worksite")}</label>
              <input value={form.work_location} onChange={(e) => setForm({ ...form, work_location: e.target.value })}
                placeholder={t("worksite_ph")} className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-location" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t("date")}</label>
              <input type="date" value={form.work_date} onChange={(e) => setForm({ ...form, work_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900" data-testid="input-date" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{t("prepared_by")} *</label>
              <input value={form.prepared_by} onChange={(e) => setForm({ ...form, prepared_by: e.target.value })}
                placeholder={t("name")} className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-prepared-by" />
            </div>
          </div>
        </div>

        {/* Section 2: Hazards */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{t("hazard_mapping")}</h2>
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold">{form.hazards.length}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {COMMON_HAZARDS.map((h) => {
              const active = form.hazards.some((x) => x.id === h.id);
              return (
                <button type="button" key={h.id} onClick={() => addHazardCommon(h)} disabled={active}
                  className={`p-2 text-xs text-left rounded border-2 transition ${active ? "bg-amber-100 border-amber-400 opacity-70" : "bg-white border-slate-200 hover:border-amber-300"}`}
                  data-testid={`hazard-btn-${h.id}`}>
                  {active && "✓ "}{L(h)}
                </button>
              );
            })}
            <button type="button" onClick={addHazardCustom}
              className="p-2 text-xs text-left rounded border-2 border-dashed border-slate-300 text-slate-500 hover:border-amber-400" data-testid="hazard-custom-btn">
              {t("custom")}
            </button>
          </div>
          {form.hazards.map((h, i) => {
            const risk = RISK_MATRIX[h.probability]?.[h.consequence] || "yellow";
            return (
              <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50" data-testid={`hazard-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-black text-slate-500">#{i + 1}</span>
                  <div className="flex items-center gap-2">
                    <span className={`${RISK_COLORS[risk]} px-2 py-1 text-xs font-bold rounded`} style={{ color: "#fff" }}>{RISK_WORD[risk][lang] || RISK_WORD[risk].no}</span>
                    <button type="button" onClick={() => removeHazard(i)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`hazard-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">{t("desc_label")}</div>
                  <textarea value={h.description} onChange={(e) => updateHazard(i, { description: e.target.value })}
                    placeholder={t("hazard_desc_ph")} rows={3} data-testid={`hazard-desc-${i}`}
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400 resize-y" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">{t("probability")}</div>
                    <div className="flex gap-1">
                      {LEVELS.map((Lv) => (
                        <button type="button" key={Lv.k} onClick={() => updateHazard(i, { probability: Lv.k })}
                          className={`flex-1 py-1 text-xs font-semibold rounded ${h.probability === Lv.k ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}
                          style={h.probability === Lv.k ? { color: "#fff" } : {}}>{Lv[lang] || Lv.no}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">{t("consequence")}</div>
                    <div className="flex gap-1">
                      {LEVELS.map((Lv) => (
                        <button type="button" key={Lv.k} onClick={() => updateHazard(i, { consequence: Lv.k })}
                          className={`flex-1 py-1 text-xs font-semibold rounded ${h.consequence === Lv.k ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}
                          style={h.consequence === Lv.k ? { color: "#fff" } : {}}>{Lv[lang] || Lv.no}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">{t("measures_label")}</div>
                  <textarea value={h.preventive_measures} onChange={(e) => updateHazard(i, { preventive_measures: e.target.value })}
                    placeholder={t("measures_ph")} rows={3} data-testid={`hazard-measures-${i}`}
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400 resize-y" />
                </div>
                <input value={h.responsible} onChange={(e) => updateHazard(i, { responsible: e.target.value })}
                  placeholder={t("responsible_ph")} className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded text-slate-900 placeholder:text-slate-400" />
              </div>
            );
          })}
        </div>

        {/* Section 3: PPE */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <h2 className="text-lg font-bold mb-3">{t("ppe")}</h2>
          <div className="grid grid-cols-4 gap-2">
            {PPE_OPTIONS.map((p) => {
              const active = form.ppe_required.includes(p.id);
              return (
                <button type="button" key={p.id} onClick={() => togglePPE(p.id)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition ${active ? "bg-emerald-50 border-emerald-500 shadow-sm" : "bg-white border-slate-200 hover:border-slate-300"}`}
                  data-testid={`ppe-${p.id}`}>
                  <span className="text-2xl">{p.icon}</span>
                  <span className="text-[10px] font-semibold text-center">{L(p)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Section 4: Emergency */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-3">
          <h2 className="text-lg font-bold">{t("emergency")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })}
              placeholder={t("emg_ph")} className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="input-emergency" />
            <input value={form.nearest_hospital} onChange={(e) => setForm({ ...form, nearest_hospital: e.target.value })}
              placeholder={t("hospital_ph")} className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" />
            <input value={form.first_aid_location} onChange={(e) => setForm({ ...form, first_aid_location: e.target.value })}
              placeholder={t("firstaid_ph")} className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" />
          </div>
          <textarea value={form.additional_measures} onChange={(e) => setForm({ ...form, additional_measures: e.target.value })}
            placeholder={t("additional_ph")} className="w-full px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" rows={2} />
        </div>

        {/* Photos */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{t("photos")}</h2>
            <div className="flex items-center gap-1">
              <button type="button" onClick={openProgressPicker} disabled={uploading}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-500 text-white text-xs font-bold rounded hover:bg-amber-600 disabled:opacity-50"
                style={{ color: "#fff" }} data-testid="sja-from-progress">
                <Camera className="w-3.5 h-3.5" /> {t("from_progress")}
              </button>
              <label className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-900 text-white text-xs font-bold rounded hover:bg-slate-800 cursor-pointer" style={{ color: "#fff" }}>
                <Camera className="w-3.5 h-3.5" /> {uploading ? "..." : t("upload")}
                <input type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => { uploadPhotos(Array.from(e.target.files || [])); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          {(form.photos || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">{t("no_photos")}</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {form.photos.map((u, pi) => (
                <div key={`${u}-${pi}`} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(pi)}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
                    data-testid={`sja-photo-remove-${pi}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={save} disabled={saving || !canSave()}
          className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white rounded-md text-sm font-bold shadow-md flex items-center justify-center gap-2"
          style={{ color: "#fff" }} data-testid="save-btn">
          {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("saving")}</> : t("save")}
        </button>
      </fieldset>

      {/* Progress-feed photo picker */}
      {progress.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setProgress((p) => ({ ...p, open: false }))}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="font-bold text-slate-900">{t("pick_photos")}</h3>
              <button type="button" onClick={() => setProgress((p) => ({ ...p, open: false }))} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {progress.loading ? (
                <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : progress.photos.length === 0 ? (
                <p className="text-center text-sm text-slate-400 italic py-10">{t("no_progress_photos")}</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {progress.photos.map((ph, i) => {
                    const on = progress.sel.has(ph.url);
                    return (
                      <button type="button" key={`${ph.url}-${i}`} onClick={() => toggleProgressSel(ph.url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 ${on ? "border-amber-500 ring-2 ring-amber-300" : "border-slate-200"}`}
                        data-testid={`sja-progress-photo-${i}`}>
                        <img src={ph.url} alt="" className="w-full h-full object-cover" />
                        {on && <span className="absolute top-1 right-1 w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center"><Check className="w-3 h-3" /></span>}
                        {ph.date && <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">{ph.date}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setProgress((p) => ({ ...p, open: false }))} className="px-4 py-2 text-sm border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
              <button type="button" onClick={addProgressSelected} disabled={progress.sel.size === 0}
                className="px-4 py-2 text-sm bg-amber-500 text-white rounded-md font-semibold disabled:opacity-40" style={{ color: "#fff" }} data-testid="sja-progress-add-selected">
                {t("add")} ({progress.sel.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import previous SJA */}
      {importer.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setImporter((s) => ({ ...s, open: false }))}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="font-bold text-slate-900">{t("import_prev")}</h3>
              <button type="button" onClick={() => setImporter((s) => ({ ...s, open: false }))} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="px-4 py-3 border-b border-slate-100">
              <input value={importer.q} onChange={(e) => setImporter((s) => ({ ...s, q: e.target.value }))}
                placeholder={t("import_search_ph")} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md text-slate-900" data-testid="sja-import-search" />
              <p className="text-[11px] text-slate-400 mt-1.5">{t("import_note")}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {importer.loading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (() => {
                const q = importer.q.toLowerCase().trim();
                const items = q ? importer.items.filter((it) => [it.code, it.address, it.customer, it.job_description].some((f) => (f || "").toLowerCase().includes(q))) : importer.items;
                if (items.length === 0) return <p className="text-center text-sm text-slate-400 italic py-10">{t("no_sja_found")}</p>;
                return (
                  <div className="space-y-2">
                    {items.map((it) => (
                      <button type="button" key={it.sja_id} onClick={() => applyImport(it)}
                        className="w-full text-left px-3 py-2.5 rounded-lg border border-slate-200 hover:border-amber-400 hover:bg-amber-50" data-testid={`sja-import-item-${it.sja_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs bg-slate-900 text-white px-1.5 py-0.5 rounded">{it.code}</span>
                          <span className="font-semibold text-sm text-slate-900 truncate">{it.job_description || it.address || "—"}</span>
                          <span className="ml-auto text-[11px] text-amber-700 font-semibold">{it.hazard_count} {t("hazards_word")}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{it.address} · {it.work_date}</div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="w-6 h-6 text-emerald-600" /></div>
              <div><h3 className="text-lg font-bold text-slate-900">{t("saved")}</h3><p className="text-xs text-slate-500 font-mono">{result.code}</p></div>
            </div>
            {[[t("sign_link_team"), `${window.location.origin}/apps/sja/sign/${result.sign_token}`, "sign"],
              [t("continue_edit"), `${window.location.origin}/apps/sja/edit/${result.edit_token}`, "edit"]].map(([label, url, k]) => (
              <div key={k} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
                <div className="flex gap-1">
                  <input readOnly value={url} className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded bg-slate-50 text-slate-700" />
                  <button type="button" onClick={() => copyLink(url, k)} className="px-2 py-1.5 bg-slate-900 rounded" style={{ color: "#fff" }}>{copied[k] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
                </div>
              </div>
            ))}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button type="button" onClick={() => { navigate(`/apps/sja/edit/${result.edit_token}`, { replace: true }); setResult(null); }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-900 rounded-md text-sm hover:bg-slate-50">{t("edit")}</button>
              <button type="button" onClick={() => window.open(`/apps/sja/sign/${result.sign_token}`, "_blank")}
                className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-medium" style={{ color: "#fff" }}>{t("open_sign")}</button>
            </div>
            <button type="button"
              onClick={() => { setForm(emptyForm()); setResult(null); setCopied({ edit: false, sign: false }); navigate("/apps/sja", { replace: true }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="w-full mt-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-sm font-semibold flex items-center justify-center gap-2" style={{ color: "#fff" }}>
              <Plus className="w-4 h-4" /> {t("start_new")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
